import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const QuestionWiseAnalysis = ({ auditData }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const questions = [
    {
      id: 'Q1',
      text: 'Are all fire extinguishers properly mounted and accessible?',
      category: 'Fire Safety',
      type: 'yes/no',
      responses: 35,
      yesCount: 33,
      noCount: 2,
      averageScore: 94,
      hasPhotos: true,
      photoCount: 31,
      flaggedCount: 2
    },
    {
      id: 'Q2',
      text: 'Is the emergency evacuation plan clearly displayed in all areas?',
      category: 'Emergency Procedures',
      type: 'yes/no',
      responses: 35,
      yesCount: 29,
      noCount: 6,
      averageScore: 83,
      hasPhotos: true,
      photoCount: 27,
      flaggedCount: 4
    },
    {
      id: 'Q3',
      text: 'Rate the overall cleanliness and organization of the work area (1-5)',
      category: 'General Safety',
      type: 'rating',
      responses: 35,
      averageRating: 4.2,
      averageScore: 84,
      hasPhotos: true,
      photoCount: 33,
      flaggedCount: 1
    },
    {
      id: 'Q4',
      text: 'Are all employees wearing appropriate PPE for their tasks?',
      category: 'PPE Compliance',
      type: 'yes/no',
      responses: 35,
      yesCount: 31,
      noCount: 4,
      averageScore: 89,
      hasPhotos: true,
      photoCount: 30,
      flaggedCount: 3
    },
    {
      id: 'Q5',
      text: 'Describe any safety hazards observed during your inspection',
      category: 'General Safety',
      type: 'text',
      responses: 35,
      averageScore: 88,
      hasPhotos: true,
      photoCount: 25,
      flaggedCount: 5
    }
  ];

  const categories = ['all', 'General Safety', 'Fire Safety', 'Emergency Procedures', 'PPE Compliance'];

  const filteredQuestions = selectedCategory === 'all' 
    ? questions 
    : questions?.filter(q => q?.category === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Category Filter */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">Filter by Category:</span>
          <div className="flex flex-wrap gap-2">
            {categories?.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                {cat === 'all' ? 'All Categories' : cat}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Questions List */}
      <div className="space-y-4">
        {filteredQuestions?.map((question) => (
          <div
            key={question?.id}
            className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-1 bg-muted text-xs font-bold text-foreground rounded">
                    {question?.id}
                  </span>
                  <span className="px-3 py-1 bg-purple-500/10 text-purple-700 text-xs font-medium rounded-full">
                    {question?.category}
                  </span>
                  <span className="px-3 py-1 bg-blue-500/10 text-blue-700 text-xs font-medium rounded-full">
                    {question?.type}
                  </span>
                </div>
                <p className="text-base font-medium text-foreground mb-3">{question?.text}</p>
                
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Users" size={14} />
                    <span>{question?.responses} responses</span>
                  </div>
                  {question?.hasPhotos && (
                    <div className="flex items-center gap-1">
                      <Icon name="Camera" size={14} />
                      <span>{question?.photoCount} photos</span>
                    </div>
                  )}
                  {question?.flaggedCount > 0 && (
                    <div className="flex items-center gap-1 text-orange-600">
                      <Icon name="Flag" size={14} />
                      <span>{question?.flaggedCount} flagged</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end">
                <div className="text-2xl font-bold text-foreground mb-1">
                  {question?.averageScore}%
                </div>
                <div className="text-xs text-muted-foreground">Avg. Score</div>
              </div>
            </div>

            {/* Response Distribution */}
            {question?.type === 'yes/no' && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">Yes</span>
                      <span className="text-sm text-muted-foreground">
                        {question?.yesCount} ({Math.round((question?.yesCount / question?.responses) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${(question?.yesCount / question?.responses) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">No</span>
                      <span className="text-sm text-muted-foreground">
                        {question?.noCount} ({Math.round((question?.noCount / question?.responses) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-red-500 h-2 rounded-full"
                        style={{ width: `${(question?.noCount / question?.responses) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {question?.type === 'rating' && (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">Average Rating</span>
                    <span className="text-sm text-muted-foreground">
                      {question?.averageRating} / 5.0
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${(question?.averageRating / 5) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-border">
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
                <Icon name="Eye" size={16} />
                View All Responses
              </button>
              {question?.hasPhotos && (
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium">
                  <Icon name="Image" size={16} />
                  View Photos
                </button>
              )}
              {question?.flaggedCount > 0 && (
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 text-orange-600 rounded-lg hover:bg-orange-500/20 transition-colors text-sm font-medium">
                  <Icon name="Flag" size={16} />
                  Review Flagged ({question?.flaggedCount})
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuestionWiseAnalysis;